/* eslint-disable max-len */
import env from 'src/env';

export type PropertySchema = {
    type: string;
    format?: string;
    description?: string;
    required?: boolean;
    requiredFields?: string[];
    enum?: (string | number)[];
    examples?: unknown[];
    properties?: Record<string, PropertySchema>;
    items?: PropertySchema;
};

export type Parameter = {
    name: string;
    in: 'path' | 'query';
    description: string;
    required: boolean;
    schema: PropertySchema;
};

export type ResponseDef = {
    statusCode: string;
    description: string;
    mediaType?: string;
    schema?: PropertySchema;
};

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export type Endpoint = {
    id: string;
    method: HttpMethod;
    path: string;
    tag: string;
    summary: string;
    description: string;
    parameters?: Parameter[];
    requestBody?: {
        mediaType: string;
        schema: PropertySchema;
    };
    responses: ResponseDef[];
    requiresAuth: boolean;
};

export type ApiSpec = {
    openapi: string;
    title: string;
    version: string;
    serverUrl: string;
    endpoints: Endpoint[];
};

const scoreSchema: PropertySchema = {
    type: 'integer',
    format: 'int32',
    examples: [50],
};

const personaScoreSchema: PropertySchema = {
    type: 'object',
    properties: {
        colour: {
            type: 'object',
            properties: {
                yellow: scoreSchema,
                green: scoreSchema,
                red: scoreSchema,
                blue: scoreSchema,
            },
        },
        aspect: {
            type: 'object',
            properties: {
                discipline_driven: scoreSchema,
                people_focused: scoreSchema,
                extraverted: scoreSchema,
                big_picture_thinking: scoreSchema,
                inspiration_driven: scoreSchema,
                outcome_focused: scoreSchema,
                introverted: scoreSchema,
                down_to_earth: scoreSchema,
            },
        },
        quality: {
            type: 'object',
            properties: {
                structured: scoreSchema,
                collaborative: scoreSchema,
                demonstrative: scoreSchema,
                imaginative: scoreSchema,
                adaptable: scoreSchema,
                tough: scoreSchema,
                observing: scoreSchema,
                practical: scoreSchema,
                reliable: scoreSchema,
                takes_charge: scoreSchema,
                radical: scoreSchema,
                empathetic: scoreSchema,
                flexible: scoreSchema,
                measured: scoreSchema,
                evidence_based: scoreSchema,
                competitive: scoreSchema,
                cautious: scoreSchema,
                spontaneous: scoreSchema,
                logical: scoreSchema,
                intimate: scoreSchema,
                conceptual: scoreSchema,
                purposeful: scoreSchema,
                accommodating: scoreSchema,
                sociable: scoreSchema,
            },
        },
    },
};

const portraitSlugs = [
    'spark-full', 'spark-intro', 'spark-workbook', 'select', 'leader',
    'leader-workbook', 'leader-360', 'leader-360-workbook', 'sales',
    'emotion', 'emotion-enhanced',
];

const participantProjectSchema: PropertySchema = {
    type: 'object',
    properties: {
        participant_type: {
            type: 'string',
            description: 'Whether Participant is a Self Assessor or Rater',
            enum: ['self', 'rater'],
        },
        project_id: {
            type: 'integer',
            format: 'int64',
            description: 'Project ID that the Participant Joined',
            examples: [12345],
        },
        project_name: {
            type: 'string',
            description: 'Project Name that the Participant Joined',
            examples: ['My Qualification Project'],
        },
        context_guid: {
            type: 'string',
            format: 'uuid',
            description: 'Self Assessor UserProjectGuid that this Participant rate, only available if participant_type is rater',
        },
    },
};

const participantSchema: PropertySchema = {
    type: 'object',
    properties: {
        participant_guid: { type: 'string', format: 'uuid', description: 'GUID of the Participant' },
        first_name: { type: 'string', description: 'Participant First Name' },
        last_name: { type: 'string', description: 'Participant Last Name' },
        email: { type: 'string', description: 'Participant Email' },
        gender_pronounce: { type: 'string', description: 'Gender pronounce of the Participant', enum: ['m', 'f'] },
        joined_projects: {
            type: 'array',
            items: participantProjectSchema,
        },
    },
};

export const apiSpec: ApiSpec = {
    openapi: '3.1.0',
    title: 'Lumina Learning API',
    version: '1.0.0',
    serverUrl: env.openApiUrl,
    endpoints: [
        {
            id: 'post-token-generate',
            method: 'POST',
            path: '/token/generate',
            tag: 'Authentication',
            summary: 'Generate JWT',
            description: 'Generate JWT to use as Bearer token in Authorization header',
            requestBody: {
                mediaType: 'application/json',
                schema: {
                    type: 'object',
                    properties: {
                        client_id: { type: 'string', description: 'API credential Client ID', required: true },
                        client_secret: { type: 'string', description: 'API credential Client secret', required: true },
                    },
                },
            },
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return JWT that can be used as Bearer token in subsequent request',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: ['OK'] },
                            jwt: { type: 'string' },
                        },
                    },
                },
                { statusCode: '403', description: 'Invalid client id or client secret' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: false,
        },
        {
            id: 'put-self-assessor',
            method: 'PUT',
            path: '/self-assessor',
            tag: 'Participant',
            summary: 'Create Participant and register as a Self Assessor',
            description: 'Create a Participant profile and register the profile to a Project as a Self Assessor',
            requestBody: {
                mediaType: 'application/json',
                schema: {
                    type: 'object',
                    properties: {
                        project_id: {
                            type: 'integer',
                            format: 'int64',
                            description: 'Project ID that the created Participant will join as Self Assessor',
                            required: true,
                            examples: [12345],
                        },
                        first_name: {
                            type: 'string',
                            description: 'First Name of the Participant profile that will be created',
                            required: true,
                            examples: ['John'],
                        },
                        last_name: {
                            type: 'string',
                            description: 'Last Name of the Participant profile that will be created',
                            required: true,
                            examples: ['Doe'],
                        },
                        email: {
                            type: 'string',
                            format: 'email',
                            description: 'Email address of the Participant profile that will be created',
                            required: true,
                            examples: ['john.doe@example.com'],
                        },
                        gender_pronounce: {
                            type: 'string',
                            description: 'Gender pronounce of the Participant, this field is optional, if its omitted then it will default to "m" (masculine pronounce)',
                            enum: ['m', 'f'],
                        },
                    },
                },
            },
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return newly created Participant GUID',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            participant_guid: { type: 'string', format: 'uuid' },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-participant',
            method: 'GET',
            path: '/participant',
            tag: 'Participant',
            summary: 'Get Participant details and joined Projects',
            description: 'Get Participant by email address, will return Participant details and joined projects',
            parameters: [
                {
                    name: 'email',
                    in: 'query',
                    description: 'Email address of the Participant',
                    required: true,
                    schema: { type: 'string' },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return Participant details and list of joined projects',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            participant: participantSchema,
                        },
                    },
                },
                { statusCode: '400', description: 'Missing input parameter' },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant not found' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-questionnaires',
            method: 'GET',
            path: '/questionnaires/{projectId}/{participantGuid}',
            tag: 'Questionnaire',
            summary: 'Get list of Participant tasks',
            description: 'Get list of questionnaires/tasks available for Participant in a particular Project',
            parameters: [
                {
                    name: 'projectId',
                    in: 'path',
                    description: 'ID of the project',
                    required: true,
                    schema: { type: 'integer', format: 'int64', examples: [12345] },
                },
                {
                    name: 'participantGuid',
                    in: 'path',
                    description: 'GUID of the Participant',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return list of questionnaires that the Participant can take',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            tasks: {
                                type: 'array',
                                items: {
                                    type: 'object',
                                    properties: {
                                        participant_guid: { type: 'string', format: 'uuid' },
                                        participant_name: { type: 'string', examples: ['John Doe'] },
                                        questionnaire_type: { type: 'string', enum: ['self', 'rater'] },
                                        questionnaire_name: { type: 'string', examples: ['Lumina Spark'] },
                                        questionnaire_id: { type: 'integer', format: 'int32', examples: [1, 14] },
                                        questionnaire_progress: { type: 'string', examples: ['0%', '20%', '100%'] },
                                    },
                                },
                            },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant not found' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-questionnaire-url',
            method: 'GET',
            path: '/questionnaire/{questionnaireId}/{projectId}/{participantGuid}',
            tag: 'Questionnaire',
            summary: 'Get URL to questionnaire/task',
            description: 'Get single use URL that the Participant can use to take the questionnaire/task',
            parameters: [
                {
                    name: 'questionnaireId',
                    in: 'path',
                    description: 'ID of the questionnaire that the Participant will take',
                    required: true,
                    schema: { type: 'integer', format: 'int32', examples: [1] },
                },
                {
                    name: 'projectId',
                    in: 'path',
                    description: 'ID of the project',
                    required: true,
                    schema: { type: 'integer', format: 'int64', examples: [12345] },
                },
                {
                    name: 'participantGuid',
                    in: 'path',
                    description: 'GUID of the Participant',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
                {
                    name: 'language_id',
                    in: 'query',
                    description: 'ID of the language that the questionnaire will be taken initially, default to English - UK (3), participant can change the language themselves before starting the questionnaire if they wish.',
                    required: false,
                    schema: { type: 'integer', format: 'int64', examples: [3] },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return single use URL to questionnaire that the Participant can take',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            url: { type: 'string', examples: ['https://www.luminalearning.com/survey/start?questionnaire_id=1&project_id=1&user_guid=3fa85f64-5717-4562-b3fc-2c963f66afa6&token=f-KE3P7We7zdguxpFq25AuF7jS-AtIoQRsIUsS9YmqXcE_IM1ygyK0UkvpE-P10e'] },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant, Project, or Questionnaire not found' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-portrait-status',
            method: 'GET',
            path: '/portrait/status/{slug}/{projectId}/{participantGuid}',
            tag: 'Portrait',
            summary: 'Request portrait PDF status',
            description: 'Get status of PDF portrait generation whether its ready, currently generating, or unavailable',
            parameters: [
                {
                    name: 'slug',
                    in: 'path',
                    description: 'Product slug that is currently being generated',
                    required: true,
                    schema: { type: 'string', enum: portraitSlugs },
                },
                {
                    name: 'projectId',
                    in: 'path',
                    description: 'ID of the project',
                    required: true,
                    schema: { type: 'integer', format: 'int64', examples: [12345] },
                },
                {
                    name: 'participantGuid',
                    in: 'path',
                    description: 'GUID of the Participant',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
                {
                    name: 'language_id',
                    in: 'query',
                    description: 'ID of the language of the PDF that is being generated',
                    required: false,
                    schema: { type: 'integer', format: 'int64', examples: [3] },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return PDF generation status',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            status: {
                                type: 'string',
                                examples: ['Available', 'Not found', 'Generating PDF', 'Generating images', 'Unsupported language'],
                            },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant, Project, or PDF not found' },
                { statusCode: '400', description: 'Bad request, invalid parameter or questionnaire incomplete' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'put-portrait-generate',
            method: 'PUT',
            path: '/portrait/generate/{slug}',
            tag: 'Portrait',
            summary: 'Request to generate PDF report',
            description: 'Request to generate PDF report, it will trigger PDF generation but will not return the PDF itself',
            parameters: [
                {
                    name: 'slug',
                    in: 'path',
                    description: 'Product slug that will be generated',
                    required: true,
                    schema: { type: 'string', enum: portraitSlugs },
                },
            ],
            requestBody: {
                mediaType: 'application/json',
                schema: {
                    type: 'object',
                    requiredFields: ['participant_guid', 'project_id'],
                    properties: {
                        participant_guid: { type: 'string', format: 'uuid', description: 'GUID of the Participant who owns the report' },
                        project_id: {
                            type: 'integer', format: 'int64', description: 'Project ID that Participant registered to take the questionnaire', examples: [12345],
                        },
                        language_id: {
                            type: 'integer', format: 'int32', description: 'Language ID that the PDF report will be generated in', examples: [1],
                        },
                    },
                },
            },
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return Portrait GUID',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            portrait_guid: { type: 'string', format: 'uuid' },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant, Project, or Product not found' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-portrait-download',
            method: 'GET',
            path: '/portrait/download/{portraitGuid}',
            tag: 'Portrait',
            summary: 'Download PDF report',
            description: 'Download Portrait PDF report if its finished generating, it will return 404 if not ready',
            parameters: [
                {
                    name: 'portraitGuid',
                    in: 'path',
                    description: 'Portrait GUID, if the PDF file is ready it will trigger download of this Portrait',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return Portrait PDF',
                    mediaType: 'application/pdf',
                    schema: { type: 'string', format: 'binary' },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'PDF portrait not found or not ready' },
                { statusCode: '400', description: 'Invalid input' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-answers',
            method: 'GET',
            path: '/answers/{questionnaireId}/{projectId}/{participantGuid}',
            tag: 'Questionnaire',
            summary: 'Get Participant questionnaire answers',
            description: 'Get Participant answers to the questionnaire',
            parameters: [
                {
                    name: 'questionnaireId',
                    in: 'path',
                    description: 'ID of the questionnaire that the Participant took',
                    required: true,
                    schema: { type: 'integer', format: 'int32', enum: [1, 21, 34] },
                },
                {
                    name: 'projectId',
                    in: 'path',
                    description: 'ID of the project',
                    required: true,
                    schema: { type: 'integer', format: 'int64', examples: [12345] },
                },
                {
                    name: 'participantGuid',
                    in: 'path',
                    description: 'GUID of the Participant',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
                {
                    name: 'language_id',
                    in: 'query',
                    description: 'ID of the language of the questions and answers that will be returned, default to English - UK',
                    required: false,
                    schema: { type: 'integer', format: 'int64', examples: [3] },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return list of questionnaire question, answer, and description',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            answers: {
                                type: 'array',
                                items: {
                                    type: 'object',
                                    properties: {
                                        question: { type: 'string', examples: ['I generally approach tasks in an organised way'] },
                                        answer: { type: 'string', examples: ['Agree'] },
                                        description: { type: 'string', examples: ['Structured - Discipline Driven'] },
                                    },
                                },
                            },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant, Project, or Questionnaire not found' },
                { statusCode: '400', description: 'Invalid parameter, questionnaire is not completed yet, or insufficient points' },
                { statusCode: '500', description: 'Server error' },
            ],
            requiresAuth: true,
        },
        {
            id: 'get-scores',
            method: 'GET',
            path: '/scores/{slug}/{projectId}/{participantGuid}',
            tag: 'Portrait',
            summary: 'Get raw scores',
            description: 'Get Participant raw scores as JSON',
            parameters: [
                {
                    name: 'slug',
                    in: 'path',
                    description: 'Product slug, currently only spark-full is supported',
                    required: true,
                    schema: { type: 'string', enum: ['spark-full'] },
                },
                {
                    name: 'projectId',
                    in: 'path',
                    description: 'ID of the project',
                    required: true,
                    schema: { type: 'integer', format: 'int64', examples: [12345] },
                },
                {
                    name: 'participantGuid',
                    in: 'path',
                    description: 'GUID of the Participant',
                    required: true,
                    schema: { type: 'string', format: 'uuid' },
                },
            ],
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response, return raw scores from all 4 Personas',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                            scores: {
                                type: 'object',
                                properties: {
                                    euo: personaScoreSchema,
                                    everyday: personaScoreSchema,
                                    underlying: personaScoreSchema,
                                    overextended: personaScoreSchema,
                                },
                            },
                        },
                    },
                },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '404', description: 'Participant, Project, or Questionnaire not found' },
                { statusCode: '400', description: 'Invalid parameter, questionnaire is not completed yet, or insufficient points' },
            ],
            requiresAuth: true,
        },
        {
            id: 'put-portrait-send',
            method: 'PUT',
            path: '/portrait/send',
            tag: 'Portrait',
            summary: 'Send PDF Portrait',
            description: 'Send PDF Portrait via Email from Lumina Learning platform',
            requestBody: {
                mediaType: 'application/json',
                schema: {
                    type: 'object',
                    properties: {
                        participant_guid: { type: 'string', format: 'uuid', description: 'GUID of the Participant who owns the report' },
                        project_id: {
                            type: 'integer', format: 'int64', description: 'Project ID that Participant registered to take the questionnaire', examples: [12345],
                        },
                        product: {
                            type: 'string',
                            description: 'Product slug',
                            enum: portraitSlugs,
                        },
                        language_id: {
                            type: 'integer', format: 'int32', description: 'Language ID that the PDF report will be generated in', examples: [1],
                        },
                    },
                },
            },
            responses: [
                {
                    statusCode: '200',
                    description: 'Success response',
                    mediaType: 'application/json',
                    schema: {
                        type: 'object',
                        properties: {
                            code: { type: 'integer', examples: [200] },
                            message: { type: 'string', examples: ['OK'] },
                        },
                    },
                },
                { statusCode: '400', description: 'Invalid input, incomplete questionnaire, or insufficient points' },
                { statusCode: '401', description: 'Authentication error, missing API key' },
                { statusCode: '403', description: 'Authorization error, invalid API key or API key provided is not allowed to access this resource' },
                { statusCode: '500', description: 'Internal server error' },
            ],
            requiresAuth: true,
        },
    ],
};

export const supportedLanguages = [
    { id: 34, name: 'Arabic', nativeName: 'العَرَبِية' },
    { id: 60, name: 'Bulgarian', nativeName: 'Bulgarian' },
    { id: 73, name: 'Chinese Simplified', nativeName: '简体中文' },
    { id: 306, name: 'Chinese Traditional', nativeName: 'Chinese Traditional' },
    { id: 78, name: 'Czech', nativeName: 'čeština' },
    { id: 81, name: 'Danish', nativeName: 'dansk' },
    { id: 20, name: 'Dutch', nativeName: 'Nederlands' },
    { id: 23, name: 'English - Canada', nativeName: 'English - Canada' },
    { id: 86, name: 'English - South Africa', nativeName: 'English - South Africa' },
    { id: 3, name: 'English - United Kingdom', nativeName: 'English - United Kingdom' },
    { id: 22, name: 'English - United States', nativeName: 'English - United States' },
    { id: 93, name: 'French - Canada', nativeName: 'français - Canada' },
    { id: 92, name: 'French - France', nativeName: 'français - France' },
    { id: 15, name: 'German', nativeName: 'Deutsch' },
    { id: 113, name: 'Hebrew', nativeName: 'עִבְרִית' },
    { id: 119, name: 'Hungarian', nativeName: 'Hungarian' },
    { id: 125, name: 'Indonesian', nativeName: 'Indonesian' },
    { id: 127, name: 'Italian', nativeName: 'Italian' },
    { id: 17, name: 'Japanese', nativeName: '日本語' },
    { id: 151, name: 'Korean', nativeName: '한국어' },
    { id: 198, name: 'Norwegian', nativeName: 'Norwegian' },
    { id: 211, name: 'Polish', nativeName: 'polski' },
    { id: 212, name: 'Portuguese', nativeName: 'português' },
    { id: 213, name: 'Portuguese - Brasil', nativeName: 'português - Brasil' },
    { id: 238, name: 'Slovak', nativeName: 'slovenčina' },
    { id: 21, name: 'Slovenian', nativeName: 'slovenščina' },
    { id: 304, name: 'Spanish - European', nativeName: 'Español - Europeo' },
    { id: 246, name: 'Spanish - Latin America', nativeName: 'español - Latinoamérica' },
    { id: 251, name: 'Swedish', nativeName: 'svenska' },
    { id: 275, name: 'Turkish', nativeName: 'Turkish' },
    { id: 277, name: 'Ukrainian', nativeName: 'Ukrainian' },
    { id: 282, name: 'Vietnamese', nativeName: 'Tiếng Việt' },
];
